import React, { useMemo } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import OperationsShell from "@/components/operations/OperationsShell";
import EngineerAdmin from "@/pages/EngineerAdmin";
import AvailabilityView from "@/components/operations/EngineerAvailabilityView";
import { useAuth } from "@/context/AuthContext";

/**
 * FIELD-001C1A · Canonical Operations Resource Management workspace.
 *
 * This surface is now STRICTLY READ-ONLY. Deep-linkable tabs
 * (`?tab=roster|workload|availability`) each render a purpose-built
 * view backed by the shared engineer roster data. Every restricted
 * create / edit / archive / configuration action lives on the
 * separate `/operations/administration/field-team` surface.
 *
 * Legacy bookmarks landing on `?tab=administration` are redirected
 * synchronously (via `<Navigate>`) so the query-string sanitiser
 * never rewrites them back to `?tab=roster` before the redirect
 * fires. See tests in `OperationsEngineers.field001c1a.test.jsx`.
 */

const TABS = [
    {
        key: "workload",
        label: "Workload",
        eyebrow: "Operations · Resource Management · Workload",
        variant: "workload",
    },
    {
        key: "availability",
        label: "Availability",
        eyebrow: "Operations · Resource Management · Availability",
        variant: "availability",
    },
];

const DEFAULT_TAB = "workload";

export default function OperationsEngineers() {
    const [params, setParams] = useSearchParams();
    const { user, hasPermission } = useAuth();
    const canManageEngineers = typeof hasPermission === "function"
        ? hasPermission("manage_engineers")
        : (Array.isArray(user?.permissions) && user.permissions.includes("manage_engineers"));

    const requested = (params.get("tab") || DEFAULT_TAB).toLowerCase();
    const isLegacyAdminAlias = requested === "administration";
    // FIELD-001C1B (clarification) · The Roster tab was removed from
    // Resource Management — Field Team Management is now the sole
    // roster surface. Legacy bookmarks landing on `?tab=roster` are
    // redirected synchronously to `?tab=workload` while preserving
    // every other query parameter, so the tab-sanitiser never races
    // and no Roster-page flicker is visible.
    const isLegacyRosterAlias = requested === "roster";

    const active = useMemo(() => {
        const found = TABS.find((t) => t.key === requested);
        return found || TABS[0];
    }, [requested]);

    // Tab-sanitiser: if the URL asked for something not in TABS,
    // rewrite to the resolved default. MUST NOT fire for the
    // legacy aliases — the synchronous <Navigate> below handles
    // those. Skipping it here prevents the previous race where the
    // sanitiser rewrote the alias back to the default before the
    // redirect landed.
    React.useEffect(() => {
        if (isLegacyAdminAlias || isLegacyRosterAlias) return;
        if (requested !== active.key) {
            const next = new URLSearchParams(params);
            next.set("tab", active.key);
            setParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requested, active.key, isLegacyAdminAlias, isLegacyRosterAlias]);

    // FIELD-001C1A · Synchronous redirect via <Navigate>. Placed
    // AFTER all hook calls to keep the hook order stable across
    // renders. React Router unmounts this component immediately on
    // the redirect, so no additional render fires with a diverging
    // hook order.
    if (isLegacyAdminAlias) {
        const next = new URLSearchParams(params);
        next.delete("tab");
        const qs = next.toString();
        return (
            <Navigate
                to={`/operations/administration/field-team${qs ? `?${qs}` : ""}`}
                replace
            />
        );
    }

    // FIELD-001C1B (clarification) · Legacy Roster alias redirect.
    // Rewrites `?tab=roster` → `?tab=workload` and preserves every
    // other query parameter verbatim.
    if (isLegacyRosterAlias) {
        const next = new URLSearchParams(params);
        next.set("tab", "workload");
        return (
            <Navigate
                to={`/operations/resources?${next.toString()}`}
                replace
            />
        );
    }

    const setTab = (key) => {
        const next = new URLSearchParams(params);
        next.set("tab", key);
        setParams(next, { replace: false });
    };

    return (
        <OperationsShell>
            <div
                data-testid="ops-engineers-page"
                data-active-tab={active.key}
                className="min-h-screen"
            >
                <TabsBar active={active.key} onChange={setTab} />
                {active.key === "availability" ? (
                    <AvailabilityView
                        canManage={canManageEngineers}
                        eyebrow={active.eyebrow}
                        backFallback="/operations"
                        title="Resource Management"
                    />
                ) : (
                    <EngineerAdmin
                        shell="operations"
                        eyebrow={active.eyebrow}
                        backFallback="/operations"
                        canManage={false}
                        variant={active.variant || "full"}
                        title="Resource Management"
                    />
                )}
            </div>
        </OperationsShell>
    );
}

function TabsBar({ active, onChange }) {
    return (
        <div className="border-b border-[#E5E7EB] bg-white px-4 md:px-6 pt-4">
            <div
                role="tablist"
                aria-label="Resource Management workspace tabs"
                data-testid="ops-engineers-tabs"
                className="flex flex-wrap gap-1"
            >
                {TABS.map((t) => {
                    const isActive = t.key === active;
                    return (
                        <Link
                            key={t.key}
                            role="tab"
                            aria-selected={isActive}
                            data-testid={`ops-engineers-tab-${t.key}`}
                            to={`?tab=${t.key}`}
                            replace={false}
                            onClick={(e) => {
                                if (
                                    e.defaultPrevented || e.button !== 0 ||
                                    e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
                                ) return;
                                e.preventDefault();
                                onChange(t.key);
                            }}
                            className={`inline-flex items-center h-11 px-4 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                                isActive
                                    ? "text-[#131B2F] border-[#3E9E2E] bg-[#F5F5F7]"
                                    : "text-[#4B5565] border-transparent hover:text-[#131B2F] hover:bg-[#F5F5F7]"
                            }`}
                        >
                            {t.label}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
